import { useState } from "react";

type ModalType = "terms" | "privacy" | "business" | null;

export default function Footer() {
  const [modal, setModal] = useState<ModalType>(null);

  const closeModal = () => setModal(null);

  return (
    <footer className="bg-stone-800 text-white">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col items-center text-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-500 rounded-lg">
              <i className="ri-shield-star-fill text-white text-sm"></i>
            </div>
            <span className="text-xl font-bold">빌리브 청소</span>
          </div>
          <p className="text-stone-400 text-sm leading-relaxed">
            20년 경력의 청소 전문가들이 제공하는 프리미엄 클리닝 서비스.<br />
            가족처럼 정직하게, 공간은 깨끗하게
          </p>
          <div className="flex gap-3">
            {[
              { icon: "ri-instagram-line", href: "#" },
              { icon: "ri-youtube-line", href: "#" },
              { icon: "ri-kakao-talk-line", href: "#" },
              { icon: "ri-article-line", href: "https://m.blog.naver.com/PostList.naver?blogId=cholxida&tab=1" },
            ].map((item) => (
              <a
                key={item.icon}
                href={item.href}
                rel={item.href.startsWith("http") ? "nofollow noopener" : undefined}
                className="w-9 h-9 flex items-center justify-center bg-stone-700 hover:bg-amber-500 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`${item.icon} text-sm`}></i>
              </a>
            ))}
          </div>
        </div>

        <div className="border-t border-stone-700 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-stone-500 text-sm">
            © 2025 빌리브 청소. All rights reserved.
          </p>
          <div className="flex gap-4">
            {[
              { label: "이용약관", key: "terms" as ModalType },
              { label: "개인정보처리방침", key: "privacy" as ModalType },
              { label: "사업자정보", key: "business" as ModalType },
            ].map((item) => (
              <a
                key={item.label}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setModal(item.key);
                }}
                className="text-stone-500 hover:text-amber-400 text-xs transition-colors cursor-pointer"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* 모달 오버레이 */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto text-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-bold text-gray-900">
                {modal === "terms" && "이용약관"}
                {modal === "privacy" && "개인정보처리방침"}
                {modal === "business" && "사업자정보"}
              </h3>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>

            {/* 내용 */}
            <div className="px-6 py-6 text-sm leading-relaxed space-y-4">
              {modal === "terms" && (
                <>
                  <p className="font-semibold text-gray-900">1. 이용약관 (빌리브 철거)</p>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제1조(목적)</p>
                      <p>본 약관은 "빌리브 철거"(이하 "회사")가 제공하는 청소 및 철거 관련 서비스의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제2조(서비스 내용)</p>
                      <p>회사는 주거공간 및 기타 공간의 정리, 청소, 철거와 관련된 서비스를 제공합니다. 서비스는 전화, 카카오톡 등 상담을 통해 진행되며, 현장 상황에 따라 작업 범위 및 조건이 달라질 수 있습니다.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제3조(이용자의 의무)</p>
                      <p>이용자는 다음 사항을 사전에 안내해야 합니다.</p>
                      <ol className="list-decimal list-inside mt-1 space-y-1 pl-2">
                        <li>작업 장소의 상태 및 특이사항</li>
                        <li>위험 요소(유리, 날카로운 물건, 유해물질 등)</li>
                        <li>보관이 필요한 물품</li>
                      </ol>
                      <p className="mt-1">이용자가 이를 고지하지 않아 발생한 문제에 대해서 회사는 책임을 지지 않습니다.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제4조(작업 및 책임 범위)</p>
                      <ol className="list-decimal list-inside space-y-1 pl-2">
                        <li>회사는 합리적인 범위 내에서 작업을 수행합니다.</li>
                        <li>현장 상태에 따라 일부 작업이 제한될 수 있습니다.</li>
                        <li>작업 중 발생할 수 있는 일반적인 손상(먼지, 경미한 오염 등)은 책임 범위에 포함되지 않습니다.</li>
                      </ol>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제5조(물품 처리)</p>
                      <ol className="list-decimal list-inside space-y-1 pl-2">
                        <li>이용자가 폐기를 요청하거나 현장에서 폐기로 판단된 물품은 처리됩니다.</li>
                        <li>처리된 물품은 원칙적으로 복구 또는 반환이 불가능합니다.</li>
                        <li>귀중품 및 보관 필요 물품은 이용자가 사전에 분리해야 합니다.</li>
                      </ol>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제6조(안전)</p>
                      <p>작업 과정에서 안전사고의 위험이 있으므로, 이용자는 작업 공간 외부에 머무는 것을 권장합니다.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제7조(비밀유지 및 개인정보)</p>
                      <p>회사는 이용자의 정보 및 작업 내용을 외부에 공개하지 않으며, 필요한 범위 내에서만 정보를 사용합니다.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제8조(면책사항)</p>
                      <p>다음 경우 회사는 책임을 지지 않습니다.</p>
                      <ol className="list-decimal list-inside mt-1 space-y-1 pl-2">
                        <li>이용자의 사전 고지 누락</li>
                        <li>물품 미분리로 인한 분실</li>
                        <li>불가항력적 상황(재해, 통제 불가 상황 등)</li>
                      </ol>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">제9조(분쟁 해결)</p>
                      <p>회사와 이용자 간 분쟁은 상호 협의하여 해결하며, 관련 법령에 따릅니다.</p>
                    </div>
                  </div>
                </>
              )}

              {modal === "privacy" && (
                <>
                  <p>빌리브 철거(이하 "회사")는 상담 및 서비스 제공을 위해 필요한 최소한의 개인정보를 수집·이용합니다.</p>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">1. 수집하는 개인정보</p>
                      <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>이름</li>
                        <li>연락처</li>
                        <li>카카오톡 ID 또는 카카오톡 상담 정보</li>
                        <li>상담 내용과 작업 관련 사진 및 문의사항</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">2. 개인정보의 이용 목적</p>
                      <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>고객 상담</li>
                        <li>작업 가능 여부 확인</li>
                        <li>일정 안내 및 연락</li>
                        <li>서비스 진행 및 사후 문의 대응</li>
                        <li>홍보</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">3. 개인정보의 보유 및 이용 기간</p>
                      <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>개인정보는 이용 목적 달성 후 지체 없이 파기합니다.</li>
                        <li>다만, 관련 법령에 따라 보관이 필요한 경우에는 해당 기간 동안 보관합니다.</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">4. 개인정보의 제3자 제공</p>
                      <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>회사는 원칙적으로 이용자의 개인정보를 외부에 제공하지 않습니다.</li>
                        <li>다만, 법령에 근거가 있거나 이용자가 동의한 경우에는 예외로 합니다.</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">5. 개인정보의 파기</p>
                      <p>보유 기간이 지난 개인정보는 지체 없이 안전한 방법으로 파기합니다.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">6. 이용자의 권리</p>
                      <p>이용자는 자신의 개인정보에 대해 열람, 정정, 삭제를 요청할 수 있습니다.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">7. 개인정보 보호책임자 및 문의</p>
                      <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>회사명: 빌리브 철거</li>
                        <li>문의 전화: 010-5560-8051</li>
                      </ul>
                    </div>
                  </div>
                </>
              )}

              {modal === "business" && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-lg">
                      <i className="ri-shield-star-fill text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">빌리브 철거</p>
                      <p className="text-xs text-gray-500">청소 · 철거 전문 서비스</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-500">상호(법인명)</span>
                      <span className="font-medium text-gray-900">빌리브 철거</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-500">사업자등록번호</span>
                      <span className="font-medium text-gray-900">798-24-01738</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-500">대표자</span>
                      <span className="font-medium text-gray-900">최지훈</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 하단 닫기 버튼 */}
            <div className="px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-2xl">
              <button
                onClick={closeModal}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 rounded-xl transition-colors cursor-pointer"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}